<template>
  <div class="page page--address">
    <Head>
      <title>{{ pageTitle }}</title>
      <body class="site--address" />
      <meta name="description" :content="currentPage.metaDescription" />
      <meta name="og:title" :content="currentPage.title" />
      <meta name="og:description" :content="currentPage.metaDescription" />
    </Head>
    <AdUnit unit="1" />
    <h1>
      <router-link :to="pathForLocale({ path: '/what-is-my-address' })">{{
        currentPage.title
      }}</router-link>
    </h1>
    <AddressSetLocation
      :getLocationOnLoad="!hasLocation"
      v-model="addressLocation"
      @error="loading = false"
    />
    <div
      v-if="$route.name === 'zone' && (loading || addressLocation)"
      class="section address-reading"
    >
      <div :class="loading ? 'loading' : ''">
        <div v-if="loading" class="spinner">
          <div class="double-bounce1"></div>
          <div class="double-bounce2"></div>
        </div>
        <div
          v-if="zone || loading"
          class="current-address"
          :class="`current-address--zone`"
        >
          {{ loading ? "&nbsp;" : `Zone ${zone.name}` }}
        </div>
        <div v-else-if="!zone">
          <p>
            What is my address could not determine the
            {{ currentPage.label }} for your location.
          </p>
        </div>
        <div v-if="zone">
          <p>
            The average annual extreme minimum temperature is between
            {{ zone.temp }}&deg; and {{ zone.temp + 5 }}&deg; F.
          </p>
        </div>
      </div>
    </div>
    <SchoolDistrictReading
      v-else-if="
        $route.name === 'schoolDistrict' && (loading || addressLocation)
      "
      :loading="loading"
      :township="township"
      :address="address"
      :schoolDistricts="schoolDistricts"
    />
    <AddressReading
      v-else-if="loading || addressLocation"
      :loading="loading"
      :page="currentPage"
      :township="township"
      :address="address"
      :location="addressLocation"
      :nativeLands="nativeLands"
    />
    <AdUnit unit="2" />
    <h2 v-if="center || loading">{{ currentPage.label || "Address" }} Map</h2>
    <USDAMap
      v-if="$route.name === 'zone' && center"
      :center="center"
      @markerMove="markerMove"
    />
    <div v-else-if="$route.name === 'nativeLands' && center">
      <iframe
        frameborder="0"
        width="100%"
        height="500"
        :src="`https://native-land.ca/wp-content/themes/Native-Land-Theme/embed/embed.html?maps=territories&position=${latLng.latitude},${latLng.longitude}`"
      ></iframe>
    </div>
    <AddressMap
      v-else-if="center"
      :center="center"
      :feature="mapFeature"
      :showNameOnHover="false"
      :showMarker="true"
      @markerMove="markerMove"
      title="Address"
    />
    <ul
      v-if="
        $route.name === 'schoolDistrict' &&
        schoolDistricts &&
        schoolDistricts.length > 1
      "
    >
      <li>
        <a @click="schoolMapType = null">View Unified School Districts</a>
      </li>
      <li>
        <a @click="schoolMapType = 'elementarySchoolDistrict'"
          >View Elementary School Districts</a
        >
      </li>
      <li>
        <a @click="schoolMapType = 'secondarySchoolDistrict'"
          >View Secondary School Districts</a
        >
      </li>
    </ul>
    <CountyDetails
      v-if="$route.name == 'county' && township"
      :fipsCode="township.fipsCode"
    />
    <Wikipedia
      v-if="$route && $route.query && $route.query.title"
      :articleTitle="$route.query.title"
    />
    <AdUnit unit="3" />
    <TheFooter :title="currentPage.subtitle" :address="false" :elevation="true">
      <div v-html="currentPage.description" />
      <template v-slot:nav>
        <li v-for="(page, pageId) in pages" :key="pageId">
          <router-link :to="{ name: pageId, query: $route.query }">{{
            page.title
          }}</router-link>
        </li>
        <li>
          <router-link :to="{ name: 'countyAtlas' }">County Map</router-link>
        </li>
      </template>
    </TheFooter>
  </div>
</template>

<script>
import { pathForLocale, round } from "@/helpers";
import AdUnit from "@/components/AdUnit.vue";
import TheFooter from "@/components/TheFooter.vue";
import AddressReading from "@/components/AddressReading.vue";
import AddressSetLocation from "@/components/AddressSetLocation.vue";
import SchoolDistrictReading from "@/components/SchoolDistrictReading.vue";
import CountyDetails from "@/components/CountyDetails.vue";
import { getTownship, getSchoolDistricts } from "@/countiesApi";
import getZone from "@/usdaApi";
import bingApi from "@/bingMaps";
import states from "@/data/states";
import axios from "axios";
import { defineAsyncComponent } from "vue";
import { Head } from "@vueuse/head";
import Wikipedia from "@/components/Wikipedia.vue";

export default {
  components: {
    AdUnit,
    TheFooter,
    AddressReading,
    AddressSetLocation,
    SchoolDistrictReading,
    AddressMap: defineAsyncComponent(() =>
      import("@/components/AddressMap.vue")
    ),
    USDAMap: defineAsyncComponent(() => import("@/components/USDAMap.vue")),
    Wikipedia,
    CountyDetails,
    Head
  },
  compatConfig: { COMPONENT_V_MODEL: false },
  data() {
    return {
      addressLocation: null,
      schoolMapType: null,
      address: null,
      township: null,
      schoolDistricts: null,
      nativeLands: null,
      loading: true,
      loadingAddress: false,
      loadingTownship: false,
      loadingSchoolDistricts: false,
      zone: null,
      loadingZone: false
    };
  },
  computed: {
    center() {
      if (this.latLng) {
        return [this.latLng.longitude, this.latLng.latitude];
      }
      return null;
    },
    latLng() {
      if (this.addressLocation && this.addressLocation.point) {
        return {
          latitude: this.addressLocation.point.coordinates[0],
          longitude: this.addressLocation.point.coordinates[1]
        };
      }
      return null;
    },
    hasLocation() {
      return (
        (this.$route.query.lat && this.$route.query.lng) ||
        this.$store.getters.hasLocation
      );
    },
    mapFeature() {
      if (this.$route.name === "schoolDistrict") {
        return this.schoolMapType ? this.schoolMapType : "schoolDistrict";
      }
      if (this.currentPage.field === "township") {
        return "township";
      }
      return this.$route.name;
    },
    pages() {
      return {
        address: {
          field: "formattedAddress",
          title: "What is my address?",
          subtitle: "What address am I at?",
          label: "Address",
          description: `<p>Find the full address and GPS coordinates of your current location (latitude and longitude).</p>
      <p>This site will help you find the address of your current location. It also will also show you the GPS coordinates of your current location (latitude and longitude), and the full address details such as the street address, county, city, state, ZIP code, and country. You can also see the street intersection to easily give verbal diretions to help someone get to your address.</p>
      <h2>What is my location?</h2>
      <p>Where am I right now? The best way to find out what the current address or GPS coordinates are of a location is to search it on this site. You can learn not just the street address, but also the intersection, city, state, ZIP code and neighborhood.</p>
      <p>What is my intersection? This is one of the most useful features of this site. This feature will help you find out what is between one or two streets that you are next to. Quickly text your friends the simplest and most accurate street name and address that is possible.</p>
      <p>What is my ZIP code? The "What Is my zip code" option under my addresses gives you the correct zip code for any address in the United States. Outside of the United States it will also correctly identify the local postal code. Other international information will also tell you where any given address is in.</p>
      <p>How do you find the county you are in? Using this page the user gets to learn which part or district (town or city) or counties you are within the general vicinity of. You should now be able to figure out your geographic location by looking it up.</p>
      <p>What is my township? This site will help you learn the name of the current township, town, or other subdivisions of the county you are in. This can be helpful for finding your municipal government.</p>
      <p>What is my neighborhood? This site will help you learn the name of the current neighborhood you are in. This can be helpful if you want to know what the street address is for a specific neighborhood, or if you want to look up your neighborhood based on your street address.</p>
      <p>Other uses of this feature of the site include seeing if you live in a neighborhood , finding out if there are other nearby neighborhoods you may not know you belong to, and the ability to search for nearby points of interest on Wikipedia.</p>
      <p>What are GPS coordinates? GPS coordinates are simply numbers that represent your exact position in space with respect to Earth when looking along fixed geographic boundaries or directions.</p>
      <h2>Where am I right now?</h2>
      <p>When it's easy to locate my place from anywhere! This web tool gives you more than just directions! There are buttons to quickly share via text (SMS), Facebook, or any other apps installed on your phone your exact street address, or look up your current address in Google maps to get directions and more easily share with others.</p>
      <p>What is my city you may ask? This information for any city location is available to find. We recommend giving this site permissions to access the GPS on your device to get the most out of this site.</p>
      <p>What is my state? All states have similar addresses of where you have moved within the boundaries in terms for your home state and / Or an office area for work. Possible states that this site can detect including in the United States and internationally. There are possible combinations like Washington or Wisconsin - they simply add up. You can get an accurate map that show the current address or city name on any US street.</p>`,
          metaDescription:
            "Find the full address and GPS coordinates of your current location (latitude and longitude). What address am I at? Find out what the current address or GPS coordinates are. You can learn not just the street address, but also the intersection, city, county, township, state, ZIP code and neighborhood."
        },
        city: {
          field: "locality",
          title: "What city am I in?",
          pageSubtitle: "Find your current city now",
          subtitle: "What is my city?",
          label: "City",
          description:
            '<p>Find what city you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude).</p><p>Search for what city you are in by address, ZIP code or location and find what city it is in. What city am I in right now? Where am I? Can I find my city based on my device location? Our data will give you your city, township, county, neighborhood and more. View your city on a map, and view where you are in the city. Drag the location of the map marker to calculate the city at any precise point.</p><p>Some reasons you might want to know your current city:</p><ul><li>Confirm your city while travelling</li><li>Find your current legal jurisdiction or municipality</li><li>Use as a real estate tool for agents or buyers</li><li>You try asking Google or Alexa or Siri what your city is, but they do not have a GPS to help you</li><li>You need to find your city and state for your sales zone.</li></ul><h3>What city am I in right now?</h3><p>Accept the location access request on your device or click "get location" to find your city from your location. Decline the request to instead search by entering your current address.</p><h3>What city am I in by zip code?</h3><p>Your zipcode is a quick way to find your approximate address, and to search for what city you are in. Click "Change" above and enter your ZIP code to see what city a given zip code is in.</p><h3>What city am I in by address</h3><p>Using your current location (from your GPS) or from searching for your address, this page will tell you where you are, including most prominently what city you are in. When you search for an address, the GPS coordinates are found for that address (or zipcode, county, etc). We then lookup what city those GPS coordinates are in to find the answer to what your location is.</p>',
          metaDescription:
            "Find what city you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude). What city am I in right now? Where am I? Our data will give you your city, township, county, neighborhood and more. View your city on a map, and view where you are in the city. Drag the location of the map marker to calculate the city at any precise point."
        },
        zipcode: {
          field: "postalCode",
          title: "What ZIP code am I in?",
          subtitle: "What is my zipcode?",
          label: "ZIP Code",
          description:
            "<p>Find what ZIP code you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude).</p>",
          metaDescription:
            "Find what ZIP code you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude)."
        },
        county: {
          field: "adminDistrict2",
          title: "What county am I in?",
          subtitle: "What is my county?",
          mapTitle: "County Map",
          label: "County",
          description:
            '<p>Find what county you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude). Need to know what county you are in? This site will tell you your county. See information about your current county and a map of counties in your state. The map shows a representation of the county you are in.</p><p>Here are a few reasons you might want to know your current county:</p><ul> <li>Confirm your county while traveling</li><li>Find your current legal jurisdiction or municipality</li><li>Use as a real estate tool for agents or buyers</li><li>You try asking Google or Alexa or Siri what your county is, but they do not have a GPS to help you</li><li>You need to find your county clerk or county office website.</li></ul><h3>What county am I in right now?</h3><p>To find your current county, you need to accept the browser request to access your device location or click "get location" above. If you do not want to share your GPS with this site you can instead search by entering your current address above.</p><h3>What county am I in by zip code?</h3><p>Your zipcode is a quick way to find your approximate location, and to search for what county you are in. Click "Change" above and enter your ZIP code to see what county a given zip code is in. Note that not all of a ZIP code is necessarily in only one county, so make sure to look at the county map for final reference. You can also drag the map marker to recalculate.</p><h3>What county am I in by address</h3><p>When you search for your address, this page will tell you where you are, first and foremost the current county you are in. When you search for an address, the GPS coordinates are found for that address (or zipcode, city, etc). We then lookup what county those GPS coordinates are located in to find the answer to where you are.</p>',
          metaDescription:
            "Find what county you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude). Need to know what county you are in? This site will tell you your county. See information about your current county and a map of counties in your state. The map shows a representation of the county you are in."
        },
        state: {
          field: "adminDistrict",
          title: "What state am I in?",
          subtitle: "What is my state?",
          mapTitle: "State Map",
          label: "State",
          description:
            "<p>Find what state you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude).</p>",
          metaDescription:
            "Find what state you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude)."
        },
        country: {
          field: "countryRegion",
          title: "What country am I in?",
          subtitle: "What is my country?",
          label: "Country",
          description:
            "<p>Find what country you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude).</p>",
          metaDescription:
            "Find what country you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude)."
        },
        neighborhood: {
          field: "neighborhood",
          title: "What neighborhood am I in?",
          subtitle: "What is my neighborhood?",
          label: "Neighborhood",
          description:
            "<p>Find what neighborhood you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude).</p>",
          metaDescription:
            "Find what neighborhood you are in based on your current location, along with your full address and GPS coordinates (latitude and longitude)."
        },
        intersection: {
          field: "intersection",
          title: "What street intersection am I at?",
          subtitle: "What is my intersection?",
          label: "Intersection",
          description:
            "<p>Find the nearest street intersection based on your current location, along with your full address and GPS coordinates (latitude and longitude).</p>",
          metaDescription:
            "Find the nearest street intersection based on your current location, along with your full address and GPS coordinates (latitude and longitude)."
        },
        nativeLands: {
          field: "nativeLands",
          title: "What Indigenous territories am I in?",
          subtitle: "What native lands am I currently in?",
          label: "Indigenous territories",
          description:
            '<p>Find the Indigenous territories you are currently on. Consider the Indigenous history of your area and reflect on the effects of colonization by exploring the native lands at your location. Data provided by <a target="_blank" href="https://native-land.ca/">Native Land</a>. Data should be considered approximate and should not by itself be considered an authoritative source.</p>',
          metaDescription:
            "Find the Indigenous territories you are currently on. Consider the Indigenous history of your area and reflect on the effects of colonization by exploring the native lands at your location."
        },
        coordinates: {
          field: "coordinates",
          title: "What are my GPS coordinates?",
          subtitle: "What GPS location am I at?",
          label: "GPS coordinates",
          description:
            "<p>Find your GPS coordinates (latitude and longitude) based on your current device location, along with your full address and more.</p>",
          metaDescription:
            "Find your GPS coordinates (latitude and longitude) based on your current device location, along with your full address and more."
        },
        township: {
          field: "township",
          title: "What township am I in?",
          subtitle: "What is my township?",
          label: "Township",
          description:
            "<p>Find your township based on your current device location, along with a map of all townships, in your county and in the United States. This includes subdivisions for your county based on United States Census data. This can include: Cities, Townships, Towns, Incorporated Places, Election Precints, Districts, Territories and other county subdivisions.</p>",
          metaDescription:
            "Find your township based on your current device location, along with a map of all townships, in your county and in the United States. This includes subdivisions for your county based on United States Census data. This can include: Cities, Townships, Towns, Incorporated Places, Election Precints, Districts, Territories and other county subdivisions."
        },
        town: {
          field: "township",
          title: "What town am I in?",
          subtitle: "What is my town?",
          label: "Town",
          description:
            "<p>Find your town based on your current device location, along with a map of all townships, in your county and in the United States. This includes subdivisions for your county based on United States Census data. This can include: Cities, Townships, Towns, Incorporated Places, Election Precints, Districts, Territories and other county subdivisions.</p>",
          metaDescription:
            "Find your town based on your current device location, along with a map of all towns, in Massachusetts, New England, and the United States. This includes subdivisions for your county based on United States Census data. This can include: Cities, Townships, Towns, Incorporated Places, Election Precints, Districts, Territories and other county subdivisions."
        },
        municipality: {
          field: "township",
          title: "What municipality am I in?",
          subtitle: "What is my municipality?",
          label: "Municipality",
          description:
            "<p>Find your municipality based on your current device location, along with a map of all municipalities, in your county and in the United States. This includes subdivisions for your county based on United States Census data. This can include: Cities, Townships, Towns, Incorporated Places, Election Precints, Districts, Territories and other county subdivisions.</p>",
          metaDescription:
            "Find your municipality based on your current device location, along with a map of all townships, in your county and in the United States. This includes subdivisions for your county based on United States Census data. This can include: Cities, Townships, Towns, Incorporated Places, Election Precints, Districts, Territories and other county subdivisions."
        },
        schoolDistrict: {
          field: "schoolDistrict",
          title: "What School District am I in?",
          subtitle: "What is my School District?",
          label: "School District",
          description:
            "<p>Find your school district based on your current device location, along with a map of all school districts, in your area and in the United States. Search to find the school district for any address in the UNited States. Find the address, phone number, and website for your school district. Find demographics and teacher student ratio, and see information on each school in your district. This includes Elementary School Districts, Secondary School Districts where applicable based on United States Census data.</p>",
          metaDescription:
            "Find your school district based on your current device location, along with a map of all school districts, in your area. Find the address, phone number, and website for your school district. Find demographics and teacher student ratio, and see information on each school in your district. This includes Elementary School Districts, Secondary School Districts where applicable based on United States Census data. "
        },
        zone: {
          field: "zone",
          title: "What Plant Hardiness Zone am I in?",
          subtitle: "What is my USDA Plant Hardiness Zone?",
          label: "Plant Hardiness Zone",
          description:
            "<p>Find your USDA Plant Hardiness Zone based on your current device location, along with a map of plant hardiness zones in your area and in the United States.</p><p>Minimum winter temperatures are a major determinant of whether a particular plant selection can be successfully grown outside year around. There are other factors that influence plant survival, such as snow cover, summer heat, humidity, soil moisture, and spring frosts.</p>",
          metaDescription:
            "Find your USDA Plant Hardiness Zone based on your current device location, along with a map of plant hardiness zones in your area and in the United States."
        }
      };
    },
    currentPage() {
      if (this.$route.name in this.pages) {
        return this.pages[this.$route.name];
      }
      return this.pages.address;
    },
    pageTitle() {
      const titleParts = [];

      if (this.$route.query.title) {
        titleParts.push(decodeURI(this.$route.query.title));
      }

      if (this.currentPage.field === "formattedAddress") {
        titleParts.push(this.$t("address-title"));
      } else {
        titleParts.push(this.currentPage.title);
      }

      if (!this.$route.query.title) {
        if (this.currentPage.field === "formattedAddress") {
          titleParts.push(this.$t("address-subtitle"));
        } else if (this.currentPage.pageSubtitle) {
          titleParts.push(this.currentPage.pageSubtitle);
        }
      }

      return titleParts.join(" - ");
    }
  },
  methods: {
    pathForLocale,
    async fetchNativeLands() {
      if (!this.latLng) {
        return;
      }
      const params = {
        maps: "territories",
        position: [this.latLng.latitude, this.latLng.longitude].join(",")
      };
      const lands = await axios.get(
        "https://native-land.ca/wp-json/nativeland/v1/api/index.php",
        { params }
      );
      this.nativeLands = lands.data;
    },
    async fetchAddress() {
      if (!this.latLng) {
        return;
      }
      this.loadingAddress = true;
      // delay showing the loading animation in case the response is super fast
      setTimeout(() => {
        if (this.loadingAddress) {
          this.loading = true;
        }
      }, 200);
      const address = await bingApi.getLocation(this.latLng);
      this.loadingAddress = false;
      const newAddress = address;
      // set state to full state name if its a US state
      if (
        address.adminDistrict &&
        address.adminDistrict.length === 2 &&
        address.adminDistrict in states
      ) {
        newAddress.adminDistrict = states[address.adminDistrict].name;
      }
      if (newAddress.adminDistrict2) {
        newAddress.adminDistrict2 = newAddress.adminDistrict2.replace(
          "Co.",
          "County"
        );
      }
      this.address = newAddress;
      this.loading = false;
    },
    async fetchTownship() {
      if (!this.latLng) {
        return;
      }
      try {
        const township = await getTownship(this.latLng);
        this.township = township;
      } catch (error) {
        this.township = null;
        // console.warn(error);
      }
    },
    async fetchSchoolDistricts() {
      if (!this.latLng) {
        return;
      }
      this.loadingSchoolDistricts = true;
      // delay showing the loading animation in case the response is super fast
      setTimeout(() => {
        if (this.loadingSchoolDistricts) {
          this.loading = true;
        }
      }, 200);
      try {
        const schoolDistricts = await getSchoolDistricts(this.latLng);
        this.schoolDistricts = schoolDistricts;
      } catch (error) {
        this.schoolDistricts = null;
        // console.warn(error);
      } finally {
        this.loadingSchoolDistricts = false;
        this.loading = false;
      }
    },
    async fetchZone() {
      if (!this.latLng) {
        return;
      }
      this.loadingZone = true;
      setTimeout(() => {
        if (this.loadingZone) {
          this.loading = true;
        }
      }, 200);
      try {
        const zone = await getZone(this.latLng);
        this.zone = zone;
      } catch (error) {
        this.zone = null;
        // console.warn(error);
      } finally {
        this.loadingZone = false;
        this.loading = false;
      }
    },
    fetchAll() {
      this.fetchAddress();
      this.fetchTownship();
      this.fetchNativeLands();
      if (this.$route.name === "schoolDistrict") {
        this.fetchSchoolDistricts();
      }
      if (this.$route.name === "zone") {
        this.fetchZone();
      }
    },
    markerMove(lngLat) {
      this.addressLocation = {
        name: "Map location",
        point: {
          coordinates: [lngLat.lat, lngLat.lng]
        }
      };
    },
    updateLocationFromRoute(route, isCurrent = false) {
      if (!route.query.lat || !route.query.lng) {
        return;
      }
      let name = "Saved location";
      if (isCurrent) {
        name = this.addressLocation.name
          ? this.addressLocation.name
          : "Current location";
      }
      if (route.query.title) {
        name = decodeURI(route.query.title);
      }
      this.addressLocation = {
        name,
        point: {
          coordinates: [route.query.lat, route.query.lng]
        }
      };
    },
    updateRoute() {
      if (typeof this.addressLocation === "undefined") {
        return;
      }
      const query = {
        lat: round(this.addressLocation.point.coordinates[0], 5),
        lng: round(this.addressLocation.point.coordinates[1], 5)
      };
      if (
        !["Current location", "Saved location", "Map location"].includes(
          this.addressLocation.name
        )
      ) {
        query.title = encodeURI(this.addressLocation.name);
      }
      this.$router.push({ query, hash: this.$route.hash });
    }
  },
  watch: {
    addressLocation() {
      this.updateRoute();
      this.fetchAll();
    },
    currentPage() {
      if (
        this.currentPage.field === "schoolDistrict" &&
        !(this.schoolDistricts || this.loadingSchoolDistricts)
      ) {
        this.fetchSchoolDistricts();
      }
      if (
        this.currentPage.field === "zone" &&
        !(this.zone || this.loadingZone)
      ) {
        this.fetchZone();
      }
    },
    $route(to, from) {
      // route name changes are already handled
      if (from.name !== to.name) {
        return;
      }
      this.updateLocationFromRoute(to, true);
    }
  },
  created() {
    if (this.$route.query.lat && this.$route.query.lng) {
      this.updateLocationFromRoute(this.$route);
    } else if (this.$store.getters.hasLocation) {
      const { location } = this.$store.state;
      this.addressLocation = {
        name: "Current location",
        point: {
          coordinates: [location.latitude, location.longitude]
        }
      };
      this.fetchAll();
    }
  }
};
</script>
